import { useEffect, useState } from 'react';
import { Activity, BarChart3, ChevronLeft, ChevronRight, ShieldAlert, Swords, TrendingUp, Trophy, Zap } from 'lucide-react';

const PART_LABELS = {
  v_head: 'Cabeza',
  v_torso: 'Torso',
  v_arm_l: 'Brazo izq.',
  v_arm_r: 'Brazo der.',
  v_leg_l: 'Pierna izq.',
  v_leg_r: 'Pierna der.'
};

function clamp(value, min = 0, max = 100) {
  return Math.max(min, Math.min(max, Number(value || 0)));
}

function formatNumber(value) {
  return new Intl.NumberFormat('es-ES').format(Number(value || 0));
}

function formatDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Sin fecha';
  return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
}

function StatCard({ label, value, hint, icon: Icon, tone = 'cyan' }) {
  const toneClass = {
    cyan: 'border-cyan-500/20 bg-cyan-500/5 text-cyan-200',
    rose: 'border-rose-500/20 bg-rose-500/5 text-rose-200',
    amber: 'border-amber-500/20 bg-amber-500/5 text-amber-200',
    emerald: 'border-emerald-500/20 bg-emerald-500/5 text-emerald-200',
    violet: 'border-violet-500/20 bg-violet-500/5 text-violet-200'
  }[tone] || 'border-cyan-500/20 bg-cyan-500/5 text-cyan-200';

  return (
    <div className={`rounded-2xl border p-4 shadow-[0_0_24px_rgba(0,0,0,0.14)] ${toneClass}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.28em] text-slate-400">{label}</p>
          <div className="mt-2 text-3xl font-black text-white">{value}</div>
          {hint && <p className="mt-2 text-xs text-slate-400">{hint}</p>}
        </div>
        <div className="rounded-xl border border-white/10 bg-black/20 p-2">
          <Icon size={18} />
        </div>
      </div>
    </div>
  );
}

function DonutChart({ value, label, subtitle }) {
  const size = 176;
  const stroke = 18;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = clamp(value, 0, 100);
  const dashOffset = circumference - (progress / 100) * circumference;

  return (
    <div className="rounded-3xl border border-slate-700 bg-[#07111f] p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.28em] text-slate-400">{label}</p>
          <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
        </div>
        <div className="text-right">
          <div className="text-4xl font-black text-white">{progress.toFixed(1)}%</div>
          <p className="text-xs uppercase tracking-[0.25em] text-cyan-300/70">Win rate</p>
        </div>
      </div>

      <div className="mt-5 flex items-center justify-center">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="drop-shadow-[0_0_18px_rgba(34,211,238,0.18)]">
          <defs>
            <linearGradient id="winRateGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#22d3ee" />
              <stop offset="100%" stopColor="#818cf8" />
            </linearGradient>
          </defs>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="rgba(148,163,184,0.18)"
            strokeWidth={stroke}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="url(#winRateGradient)"
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
          <text x="50%" y="47%" textAnchor="middle" fill="#e2e8f0" fontSize="18" fontWeight="800">
            {progress.toFixed(1)}%
          </text>
          <text x="50%" y="58%" textAnchor="middle" fill="#94a3b8" fontSize="10" letterSpacing="2">
            COMBATES
          </text>
        </svg>
      </div>
    </div>
  );
}

function TrendChart({ title, subtitle, data }) {
  const values = data || [];
  const width = 760;
  const height = 240;
  const padding = 22;
  const maxY = Math.max(1, ...values.map((entry) => Number(entry.battles || 0)), ...values.map((entry) => Number(entry.experience || 0) / 10));
  const points = values.map((entry, index) => {
    const x = padding + (index * ((width - padding * 2) / Math.max(1, values.length - 1)));
    const y = height - padding - ((Number(entry.battles || 0) / maxY) * (height - padding * 2));
    return `${x},${y}`;
  }).join(' ');

  return (
    <div className="rounded-3xl border border-slate-700 bg-[#07111f] p-5">
      <div className="flex items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.28em] text-slate-400">{title}</p>
          <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-400">
          <span className="inline-flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-cyan-400" /> Batallas
          </span>
          <span className="inline-flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-emerald-400" /> XP
          </span>
        </div>
      </div>

      <div className="mt-4 overflow-hidden rounded-2xl border border-slate-700 bg-[#060b16] p-3">
        <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-[240px]">
          <defs>
            <linearGradient id="trendFill" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgba(34,211,238,0.38)" />
              <stop offset="100%" stopColor="rgba(34,211,238,0)" />
            </linearGradient>
          </defs>
          {[0.25, 0.5, 0.75].map((line) => (
            <line
              key={line}
              x1={padding}
              x2={width - padding}
              y1={padding + (height - padding * 2) * line}
              y2={padding + (height - padding * 2) * line}
              stroke="rgba(148,163,184,0.12)"
              strokeDasharray="4 4"
            />
          ))}
          <polyline
            fill="url(#trendFill)"
            points={`${points} ${width - padding},${height - padding} ${padding},${height - padding}`}
            stroke="none"
          />
          <polyline
            fill="none"
            points={points}
            stroke="#22d3ee"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {values.map((entry, index) => {
            const x = padding + (index * ((width - padding * 2) / Math.max(1, values.length - 1)));
            const y = height - padding - ((Number(entry.battles || 0) / maxY) * (height - padding * 2));
            return (
              <g key={entry.date || index}>
                <circle cx={x} cy={y} r="4" fill="#22d3ee" />
                <title>{`${entry.date}: ${entry.battles} batallas`}</title>
              </g>
            );
          })}
        </svg>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {values.slice(-4).map((entry) => (
          <div key={entry.date} className="rounded-xl border border-slate-700 bg-black/20 p-3">
            <p className="text-[11px] uppercase tracking-[0.25em] text-slate-500">{formatDate(entry.date)}</p>
            <p className="mt-2 text-2xl font-black text-white">{Number(entry.battles || 0)}</p>
            <p className="text-xs text-slate-400">Batallas</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function DamageBars({ title, damage, kind }) {
  const entries = Object.entries(kind === 'received' ? damage.receivedByPart || {} : damage.dealtByPart || {})
    .sort((a, b) => Number(b[1] || 0) - Number(a[1] || 0));
  const max = Math.max(1, ...entries.map((entry) => Number(entry[1] || 0)));

  return (
    <div className="rounded-3xl border border-slate-700 bg-[#07111f] p-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.28em] text-slate-400">{title}</p>
          <p className="mt-1 text-sm text-slate-500">
            {kind === 'received' ? 'Zonas que más castigan al bot' : 'Zonas donde más castigas al enemigo'}
          </p>
        </div>
        <div className="rounded-xl border border-white/10 bg-black/20 p-2 text-slate-300">
          <BarChart3 size={18} />
        </div>
      </div>

      <div className="mt-4 space-y-3">
        {entries.length > 0 ? entries.map(([part, value]) => {
          const percent = Math.max(3, (Number(value || 0) / max) * 100);
          return (
            <div key={part}>
              <div className="mb-1 flex items-center justify-between text-sm">
                <span className="text-slate-200">{PART_LABELS[part] || part}</span>
                <span className="font-semibold text-white">{formatNumber(value)}</span>
              </div>
              <div className="h-3 rounded-full bg-slate-800 overflow-hidden">
                <div
                  className={kind === 'received' ? 'h-full bg-gradient-to-r from-rose-500 to-amber-400' : 'h-full bg-gradient-to-r from-cyan-400 to-emerald-400'}
                  style={{ width: `${percent}%` }}
                />
              </div>
            </div>
          );
        }) : (
          <div className="rounded-2xl border border-dashed border-slate-700 bg-black/20 p-4 text-sm text-slate-500">
            Todavía no hay suficiente historial detallado para dibujar esta gráfica.
          </div>
        )}
      </div>
    </div>
  );
}

function RadarChart({ title, subtitle, data, tone = 'cyan' }) {
  const size = 280;
  const center = size / 2;
  const radius = 92;
  const items = data || [];
  const fill = tone === 'rose' ? 'rgba(251,113,133,0.2)' : 'rgba(34,211,238,0.2)';
  const stroke = tone === 'rose' ? '#fb7185' : '#22d3ee';
  const ringStroke = tone === 'rose' ? 'rgba(251,113,133,0.18)' : 'rgba(34,211,238,0.18)';
  const points = items.map((entry, index) => {
    const angle = (-Math.PI / 2) + ((Math.PI * 2 * index) / Math.max(1, items.length));
    const distance = radius * (Number(entry.score || 0) / 100);
    return {
      x: center + Math.cos(angle) * distance,
      y: center + Math.sin(angle) * distance,
      labelX: center + Math.cos(angle) * (radius + 18),
      labelY: center + Math.sin(angle) * (radius + 18),
      ...entry
    };
  });
  const polygonPoints = points.map((point) => `${point.x},${point.y}`).join(' ');

  return (
    <div className="rounded-3xl border border-slate-700 bg-[#07111f] p-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.28em] text-slate-400">{title}</p>
          <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
        </div>
        <div className="rounded-xl border border-white/10 bg-black/20 p-2 text-slate-300">
          <BarChart3 size={18} />
        </div>
      </div>

      <div className="mt-4 overflow-hidden rounded-2xl border border-slate-700 bg-[#060b16] p-3">
        <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-[280px]">
          {[0.25, 0.5, 0.75, 1].map((factor) => (
            <circle
              key={factor}
              cx={center}
              cy={center}
              r={radius * factor}
              fill="none"
              stroke={ringStroke}
              strokeDasharray="4 4"
            />
          ))}
          {points.map((point) => (
            <line
              key={`${point.part}-line`}
              x1={center}
              y1={center}
              x2={point.labelX}
              y2={point.labelY}
              stroke="rgba(148,163,184,0.12)"
            />
          ))}
          <polygon points={polygonPoints} fill={fill} stroke={stroke} strokeWidth="2.5" strokeLinejoin="round" />
          {points.map((point) => (
            <g key={point.part}>
              <circle cx={point.x} cy={point.y} r="4" fill={stroke} />
              <text
                x={point.labelX}
                y={point.labelY}
                textAnchor="middle"
                dominantBaseline="middle"
                fill="#cbd5e1"
                fontSize="9"
                letterSpacing="1"
              >
                {PART_LABELS[point.part] || point.part}
              </text>
            </g>
          ))}
        </svg>
      </div>
    </div>
  );
}

function SankeyChart({ sankey }) {
  const nodes = sankey?.nodes || [];
  const links = sankey?.links || [];
  const leftNodes = nodes.filter((node) => node.side === 'left');
  const rightNodes = nodes.filter((node) => node.side === 'right');
  const maxValue = Math.max(1, ...links.map((link) => Number(link.value || 0)));
  const width = 760;
  const height = Math.max(280, Math.max(leftNodes.length, rightNodes.length) * 48 + 80);
  const leftPositions = new Map(leftNodes.map((node, index) => [node.id, 40 + (index * ((height - 80) / Math.max(1, leftNodes.length - 1 || 1)))]));
  const rightPositions = new Map(rightNodes.map((node, index) => [node.id, 40 + (index * ((height - 80) / Math.max(1, rightNodes.length - 1 || 1)))]));

  return (
    <div className="rounded-3xl border border-slate-700 bg-[#07111f] p-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.28em] text-slate-400">Sankey de daño</p>
          <p className="mt-1 text-sm text-slate-500">Ataques enemigos y del bot distribuidos por parte afectada</p>
        </div>
        <div className="rounded-xl border border-white/10 bg-black/20 p-2 text-slate-300">
          <Swords size={18} />
        </div>
      </div>

      <div className="mt-4 overflow-hidden rounded-2xl border border-slate-700 bg-[#060b16] p-3">
        <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-[320px]">
          {links.map((link, index) => {
            const sourceY = leftPositions.get(link.source) || 40;
            const targetY = rightPositions.get(link.target) || 40;
            const sourceX = 120;
            const targetX = width - 120;
            const strokeWidth = Math.max(1.5, (Number(link.value || 0) / maxValue) * 18);
            const path = `M ${sourceX} ${sourceY} C ${sourceX + 120} ${sourceY}, ${targetX - 120} ${targetY}, ${targetX} ${targetY}`;
            return (
              <path
                key={`${link.source}-${link.target}-${index}`}
                d={path}
                fill="none"
                stroke={link.source === 'enemy' ? 'rgba(251,113,133,0.45)' : 'rgba(34,211,238,0.45)'}
                strokeWidth={strokeWidth}
                strokeLinecap="round"
              />
            );
          })}

          {leftNodes.map((node) => {
            const y = leftPositions.get(node.id) || 40;
            return (
              <g key={node.id}>
                <rect x="8" y={y - 13} width="94" height="26" rx="10" fill="rgba(2,6,23,0.9)" stroke="rgba(148,163,184,0.18)" />
                <text x="55" y={y + 1} textAnchor="middle" fill="#e2e8f0" fontSize="10">{node.label}</text>
              </g>
            );
          })}

          {rightNodes.map((node) => {
            const y = rightPositions.get(node.id) || 40;
            return (
              <g key={node.id}>
                <rect x={width - 102} y={y - 13} width="94" height="26" rx="10" fill="rgba(2,6,23,0.9)" stroke="rgba(148,163,184,0.18)" />
                <text x={width - 55} y={y + 1} textAnchor="middle" fill="#e2e8f0" fontSize="10">{node.label}</text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}

function LocationCards({ cards }) {
  return (
    <div className="rounded-3xl border border-slate-700 bg-[#07111f] p-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.28em] text-slate-400">Comparativa por mapa</p>
          <p className="mt-1 text-sm text-slate-500">Sector y coordenada con mejor rendimiento y actividad</p>
        </div>
        <div className="rounded-xl border border-white/10 bg-black/20 p-2 text-slate-300">
          <BarChart3 size={18} />
        </div>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        {cards.length > 0 ? cards.map((card) => (
          <div key={`${card.sectorId}-${card.coordinate}`} className="rounded-2xl border border-slate-700 bg-black/15 p-4">
            <p className="text-xs uppercase tracking-[0.25em] text-slate-500">Sector #{card.sectorId} · Coord {card.coordinate}</p>
            <div className="mt-2 text-2xl font-black text-white">{card.battles}</div>
            <div className="text-xs text-slate-400">combates · win rate {card.winRate.toFixed(1)}%</div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-400">
              <div className="rounded-xl border border-slate-700 bg-slate-950/40 p-2">XP: <span className="text-cyan-200 font-semibold">{formatNumber(card.experience)}</span></div>
              <div className="rounded-xl border border-slate-700 bg-slate-950/40 p-2">Oro: <span className="text-amber-200 font-semibold">{formatNumber(card.money)}</span></div>
            </div>
          </div>
        )) : (
          <div className="rounded-2xl border border-dashed border-slate-700 bg-black/20 p-4 text-sm text-slate-500 md:col-span-2 xl:col-span-4">
            Todavía no hay suficientes combates para comparar sectores y coordenadas.
          </div>
        )}
      </div>
    </div>
  );
}

function HeatmapGrid({ cells }) {
  return (
    <div className="rounded-3xl border border-slate-700 bg-[#07111f] p-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.28em] text-slate-400">Mapa de calor táctico</p>
          <p className="mt-1 text-sm text-slate-500">Sector, coordenada y franja horaria con mayor presión</p>
        </div>
        <div className="rounded-xl border border-white/10 bg-black/20 p-2 text-slate-300">
          <Activity size={18} />
        </div>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        {cells.length > 0 ? cells.map((cell) => (
          <div
            key={`${cell.sectorId}-${cell.coordinate}-${cell.hourBand}`}
            className="rounded-2xl border border-slate-700 p-4"
            style={{
              background: `linear-gradient(180deg, rgba(2,6,23,0.95), rgba(2,6,23,0.88)), rgba(34,211,238,${Math.min(0.28, cell.intensity / 500)})`
            }}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-slate-500">Sector #{cell.sectorId}</p>
                <p className="mt-2 text-lg font-bold text-white">Coord {cell.coordinate}</p>
              </div>
              <span className="rounded-full border border-cyan-400/20 bg-cyan-500/10 px-2 py-1 text-[10px] uppercase tracking-[0.22em] text-cyan-200">
                {cell.hourBand}
              </span>
            </div>
            <div className="mt-3 text-3xl font-black text-white">{cell.battles}</div>
            <div className="text-xs text-slate-400">combates en esta franja</div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-400">
              <div className="rounded-xl border border-slate-700 bg-slate-950/50 p-2">XP: <span className="text-cyan-200 font-semibold">{formatNumber(cell.experience)}</span></div>
              <div className="rounded-xl border border-slate-700 bg-slate-950/50 p-2">Win: <span className="text-emerald-200 font-semibold">{cell.battles > 0 ? `${Math.round((cell.victories / cell.battles) * 1000) / 10}%` : '0%'}</span></div>
            </div>
          </div>
        )) : (
          <div className="rounded-2xl border border-dashed border-slate-700 bg-black/20 p-4 text-sm text-slate-500 md:col-span-2 xl:col-span-4">
            Todavía no hay suficiente actividad para construir el mapa de calor.
          </div>
        )}
      </div>
    </div>
  );
}

function EnemyRankings({ rankings }) {
  const panels = [
    { title: 'Frecuencia', items: rankings.byFrequency || [], metric: 'battles', metricLabel: 'Combates' },
    { title: 'Daño recibido', items: rankings.byDamageReceived || [], metric: 'damageReceived', metricLabel: 'Daño' },
    { title: 'Tiempo de combate', items: rankings.byCombatTime || [], metric: 'avgDurationSeconds', metricLabel: 'Seg.' }
  ];

  return (
    <div className="rounded-3xl border border-slate-700 bg-[#07111f] p-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.28em] text-slate-400">Top enemigos</p>
          <p className="mt-1 text-sm text-slate-500">Frecuencia, daño recibido y tiempo de combate</p>
        </div>
        <div className="rounded-xl border border-white/10 bg-black/20 p-2 text-slate-300">
          <Swords size={18} />
        </div>
      </div>

      <div className="mt-4 grid gap-3 xl:grid-cols-3">
        {panels.map((panel) => (
          <div key={panel.title} className="rounded-2xl border border-slate-700 bg-black/15 p-4">
            <p className="text-xs uppercase tracking-[0.25em] text-slate-500">{panel.title}</p>
            <div className="mt-3 space-y-2">
              {panel.items.length > 0 ? panel.items.map((enemy, index) => (
                <div key={`${panel.title}-${enemy.id}`} className="rounded-xl border border-slate-700 bg-slate-950/40 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-semibold text-white">#{index + 1} {enemy.name}</div>
                    <div className="text-xs text-slate-400">{panel.metricLabel}: <span className="text-cyan-200 font-semibold">{formatNumber(enemy[panel.metric])}{panel.metric === 'avgDurationSeconds' ? 's' : ''}</span></div>
                  </div>
                  <div className="mt-1 text-xs text-slate-500">Nv. {enemy.level} · Win rate {enemy.winRate.toFixed(1)}%</div>
                </div>
              )) : (
                <div className="rounded-xl border border-dashed border-slate-700 bg-black/20 p-3 text-xs text-slate-500">
                  Sin datos para este ranking.
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EnemyTable({ enemies }) {
  return (
    <div className="rounded-3xl border border-slate-700 bg-[#07111f] p-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.28em] text-slate-400">Enemigos más enfrentados</p>
          <p className="mt-1 text-sm text-slate-500">Historial resumido por plantilla enemiga</p>
        </div>
        <div className="rounded-xl border border-white/10 bg-black/20 p-2 text-slate-300">
          <Swords size={18} />
        </div>
      </div>

      <div className="mt-4 overflow-hidden rounded-2xl border border-slate-700">
        <table className="w-full text-sm">
          <thead className="bg-slate-900/80 text-slate-400 uppercase tracking-[0.18em] text-[10px]">
            <tr>
              <th className="px-3 py-3 text-left">Enemigo</th>
              <th className="px-3 py-3 text-left">Nivel</th>
              <th className="px-3 py-3 text-left">Combates</th>
              <th className="px-3 py-3 text-left">Win rate</th>
            </tr>
          </thead>
          <tbody>
            {enemies.length > 0 ? enemies.map((enemy) => (
              <tr key={enemy.id} className="border-t border-slate-800/80 bg-black/10">
                <td className="px-3 py-3">
                  <div className="font-semibold text-white">{enemy.name}</div>
                  <div className="text-xs text-slate-500">Último combate: {formatDate(enemy.lastFightAt)}</div>
                </td>
                <td className="px-3 py-3 text-cyan-200">Nv. {enemy.level}</td>
                <td className="px-3 py-3 text-white">{enemy.battles}</td>
                <td className="px-3 py-3 text-emerald-300">{enemy.winRate.toFixed(1)}%</td>
              </tr>
            )) : (
              <tr>
                <td colSpan="4" className="px-3 py-6 text-center text-slate-500">
                  Aún no hay enfrentamientos registrados.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RecentBattles({ battles }) {
  const pageSize = 5;
  const [page, setPage] = useState(0);

  useEffect(() => {
    setPage(0);
  }, [battles.length]);

  const totalPages = Math.max(1, Math.ceil(battles.length / pageSize));
  const currentPage = Math.min(page, totalPages - 1);
  const start = currentPage * pageSize;
  const pageBattles = battles.slice(start, start + pageSize);
  const startLabel = battles.length === 0 ? 0 : start + 1;
  const endLabel = Math.min(start + pageSize, battles.length);

  return (
    <div className="rounded-3xl border border-slate-700 bg-[#07111f] p-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.28em] text-slate-400">Últimos combates</p>
          <p className="mt-1 text-sm text-slate-500">Registro reciente con sector, daño y recompensa</p>
        </div>
        <div className="rounded-xl border border-white/10 bg-black/20 p-2 text-slate-300">
          <Activity size={18} />
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between gap-3 text-xs text-slate-500">
        <span>
          {battles.length > 0
            ? `Mostrando ${startLabel}-${endLabel} de ${battles.length}`
            : 'No hay combates recientes para mostrar'}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setPage((value) => Math.max(0, value - 1))}
            disabled={currentPage === 0}
            className="inline-flex items-center gap-1 rounded-lg border border-slate-700 bg-black/20 px-3 py-2 text-slate-200 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronLeft size={14} /> Anterior
          </button>
          <button
            type="button"
            onClick={() => setPage((value) => Math.min(totalPages - 1, value + 1))}
            disabled={currentPage >= totalPages - 1}
            className="inline-flex items-center gap-1 rounded-lg border border-slate-700 bg-black/20 px-3 py-2 text-slate-200 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Siguiente <ChevronRight size={14} />
          </button>
        </div>
      </div>

      <div className="mt-3 max-h-[32rem] space-y-3 overflow-y-auto pr-1">
        {pageBattles.length > 0 ? pageBattles.map((battle) => (
          <div key={battle.id} className="rounded-2xl border border-slate-700 bg-black/15 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.25em] ${battle.outcome === 'victory' ? 'bg-emerald-500/15 text-emerald-300' : 'bg-rose-500/15 text-rose-300'}`}>
                    {battle.outcome === 'victory' ? 'Victoria' : 'Derrota'}
                  </span>
                  <span className="text-white font-semibold">{battle.enemy.name}</span>
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  Sector #{battle.sectorId} · Coord {battle.coordinate} · {formatDate(battle.date)}
                </p>
              </div>
              <div className="grid grid-cols-3 gap-3 text-right text-xs">
                <div>
                  <p className="text-slate-500">Rondas</p>
                  <p className="text-white font-semibold">{battle.rounds || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500">XP</p>
                  <p className="text-cyan-200 font-semibold">{formatNumber(battle.experienceGained)}</p>
                </div>
                <div>
                  <p className="text-slate-500">Oro</p>
                  <p className="text-amber-200 font-semibold">{formatNumber(battle.moneyGained)}</p>
                </div>
              </div>
            </div>
            <div className="mt-3 grid gap-2 md:grid-cols-2 text-xs text-slate-400">
              <div className="rounded-xl border border-slate-700/70 bg-slate-950/50 p-3">
                Daño infligido: <span className="text-cyan-200 font-semibold">{formatNumber(battle.damageDealt)}</span>
              </div>
              <div className="rounded-xl border border-slate-700/70 bg-slate-950/50 p-3">
                Daño recibido: <span className="text-rose-200 font-semibold">{formatNumber(battle.damageReceived)}</span>
              </div>
            </div>
          </div>
        )) : (
          <div className="rounded-2xl border border-dashed border-slate-700 bg-black/20 p-4 text-sm text-slate-500">
            No hay combates recientes para mostrar.
          </div>
        )}
      </div>
    </div>
  );
}

export default function BattleStatsSection({ stats, loading = false, error = '', onRefresh = null }) {
  if (loading) {
    return (
      <section className="rounded-3xl border border-slate-700 bg-[#06111d] p-6">
        <p className="text-sm text-slate-400">Cargando estadísticas de combate...</p>
      </section>
    );
  }

  if (error) {
    return (
      <section className="rounded-3xl border border-rose-500/20 bg-rose-500/5 p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.28em] text-rose-300/70">Estadísticas de batalla</p>
            <p className="mt-2 text-white">{error}</p>
          </div>
          {onRefresh && (
            <button
              type="button"
              onClick={onRefresh}
              className="rounded-xl border border-rose-400/30 bg-rose-500/10 px-4 py-2 text-sm text-rose-100 hover:bg-rose-500/20"
            >
              Reintentar
            </button>
          )}
        </div>
      </section>
    );
  }

  const overview = stats?.overview || {};
  const damage = stats?.damage || {};
  const radar = stats?.radar || {};
  const sankey = stats?.sankey || {};
  const timeline = stats?.timelines || [];
  const enemies = stats?.enemies || [];
  const locationCards = stats?.locationCards || [];
  const heatmapCells = stats?.heatmapCells || [];
  const enemyRankings = stats?.enemyRankings || {};
  const recentBattles = stats?.recentBattles || [];
  const bot = stats?.bot || {};

  if (!stats) {
    return (
      <section className="rounded-3xl border border-slate-700 bg-[#06111d] p-6">
        <p className="text-sm text-slate-400">Sin estadísticas de combate disponibles todavía.</p>
      </section>
    );
  }

  return (
    <section className="rounded-[2rem] border border-cyan-500/20 bg-[linear-gradient(180deg,rgba(7,17,31,0.98),rgba(4,8,16,0.98))] p-5 shadow-[0_0_50px_rgba(34,211,238,0.08)]">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-500/10 px-3 py-1 text-[11px] uppercase tracking-[0.3em] text-cyan-200">
            <Trophy size={13} /> Inteligencia táctica
          </div>
          <h2 className="mt-3 text-2xl md:text-3xl font-black text-white">Estadísticas de batalla del BOT</h2>
          <p className="mt-2 max-w-3xl text-sm text-slate-400">
            Análisis consolidado desde los reportes de combate: victorias, daño por parte, rivales más peligrosos, tendencia temporal y rendimiento económico.
          </p>
        </div>

        <div className="rounded-2xl border border-slate-700 bg-black/20 px-4 py-3 text-right">
          <p className="text-[11px] uppercase tracking-[0.28em] text-slate-500">Bot activo</p>
          <p className="mt-1 text-lg font-bold text-white">{bot.name || 'Sin bot'}</p>
          <p className="text-xs text-slate-400">Nv. {bot.level || 1} · Rank {stats.leaderboard?.rank || 'N/A'}</p>
        </div>
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Combates" value={formatNumber(overview.totalBattles)} hint="Historial total" icon={Swords} tone="cyan" />
        <StatCard label="Victorias" value={formatNumber(overview.victories)} hint={`Racha actual: ${overview.currentWinStreak || 0}`} icon={Trophy} tone="emerald" />
        <StatCard label="Derrotas" value={formatNumber(overview.defeats)} hint={`Win rate: ${Number(overview.winRate || 0).toFixed(1)}%`} icon={ShieldAlert} tone="rose" />
        <StatCard label="Recompensa total" value={`${formatNumber(overview.totalExperience)} XP`} hint={`${formatNumber(overview.totalMoney)} oro`} icon={Zap} tone="amber" />
      </div>

      <div className="mt-5 grid gap-5 xl:grid-cols-[360px_1fr]">
        <DonutChart
          value={overview.winRate || 0}
          label="Eficiencia de combate"
          subtitle="Victoria frente a total de registros"
        />
        <TrendChart
          title="Tendencia de 14 días"
          subtitle="Batallas registradas por día"
          data={timeline}
        />
      </div>

      <div className="mt-5 grid gap-5 xl:grid-cols-2">
        <DamageBars title="Daño infligido" damage={damage} kind="dealt" />
        <DamageBars title="Daño recibido" damage={damage} kind="received" />
      </div>

      <div className="mt-5 grid gap-5 xl:grid-cols-2">
        <RadarChart
          title="Radar de atributos de combate"
          subtitle="Potencia ofensiva por zonas del bot"
          data={radar.offense || []}
        />
        <RadarChart
          title="Radar de vulnerabilidades"
          subtitle="Presión recibida por zonas del bot"
          data={radar.vulnerability || []}
          tone="rose"
        />
      </div>

      <div className="mt-5">
        <SankeyChart sankey={sankey} />
      </div>

      <div className="mt-5">
        <HeatmapGrid cells={heatmapCells} />
      </div>

      <div className="mt-5">
        <LocationCards cards={locationCards} />
      </div>

      <div className="mt-5 grid gap-5 xl:grid-cols-[1.1fr_0.9fr]">
        <EnemyTable enemies={enemies} />
        <div className="space-y-5">
          <div className="rounded-3xl border border-slate-700 bg-[#07111f] p-5">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-[0.28em] text-slate-400">Lectura táctica</p>
                <p className="mt-1 text-sm text-slate-500">Resumen de comportamiento del bot en combate</p>
              </div>
              <div className="rounded-xl border border-white/10 bg-black/20 p-2 text-slate-300">
                <TrendingUp size={18} />
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-2xl border border-slate-700 bg-black/15 p-4">
                <p className="text-xs uppercase tracking-[0.25em] text-slate-500">Mejor parte ofensiva</p>
                <p className="mt-2 text-lg font-bold text-cyan-200">{damage.topDealtPart ? PART_LABELS[damage.topDealtPart.part] || damage.topDealtPart.part : 'N/A'}</p>
                <p className="text-xs text-slate-400 mt-1">{formatNumber(damage.topDealtPart?.value || 0)} de daño acumulado</p>
              </div>
              <div className="rounded-2xl border border-slate-700 bg-black/15 p-4">
                <p className="text-xs uppercase tracking-[0.25em] text-slate-500">Punto más castigado</p>
                <p className="mt-2 text-lg font-bold text-rose-200">{damage.topReceivedPart ? PART_LABELS[damage.topReceivedPart.part] || damage.topReceivedPart.part : 'N/A'}</p>
                <p className="text-xs text-slate-400 mt-1">{formatNumber(damage.topReceivedPart?.value || 0)} de daño recibido</p>
              </div>
              <div className="rounded-2xl border border-slate-700 bg-black/15 p-4">
                <p className="text-xs uppercase tracking-[0.25em] text-slate-500">Media de rondas</p>
                <p className="mt-2 text-lg font-bold text-white">{Number(overview.avgRounds || 0).toFixed(1)}</p>
              </div>
              <div className="rounded-2xl border border-slate-700 bg-black/15 p-4">
                <p className="text-xs uppercase tracking-[0.25em] text-slate-500">Media de turnos</p>
                <p className="mt-2 text-lg font-bold text-white">{Number(overview.avgTurns || 0).toFixed(1)}</p>
              </div>
            </div>
          </div>

          <RecentBattles battles={recentBattles} />
        </div>
      </div>

      <div className="mt-5">
        <EnemyRankings rankings={enemyRankings} />
      </div>
    </section>
  );
}
